/**
 * تنسيق الأرقام والتواريخ والمواعيد.
 *
 * ⛔ قاعدتان مقفولتان بيتنفّذوا هنا وبس:
 *   1) أرقام لاتينية (0-9) في كل الواجهة — عن طريق locale بـ`-u-nu-latn`.
 *   2) ⚠️ فخّ اتكسر قبل كده: <bdi dir="ltr"> جواها حرف عربي **بتقلب الترتيب**.
 *      «6م – 8م» بتتقرا «م – 8م6» = حجز في وقت غلط.
 *      ⇒ أي نص فيه حرف عربي (المواعيد بـ«ص/م») بيتلفّ في <bdi> **بلا dir**.
 *      الـdir="ltr" للاتيني الصافي بس (تليفون · إيميل · معرّف).
 */
import type { ReactElement } from "react";

/** locale بأرقام لاتينية إجباريًا */
const LOC = "ar-EG-u-nu-latn";

/** توقيت العرض الافتراضي — الطلاب في كندا وفرنسا وبريطانيا وأمريكا ومصر،
 *  فالتوقيت جزء من المشكلة مش تفصيلة. بيتاخد من المتصفح ما لم يتحدَّد. */
export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "Africa/Cairo";
}

export function fmtNum(n: number, opts: Intl.NumberFormatOptions = {}): string {
  return new Intl.NumberFormat(LOC, opts).format(n);
}

/** رقم داخل عنصر بخط الأرقام الثابت — الاستخدام العادي في الواجهة */
export function Num({ v }: { v: number | string }): ReactElement {
  return <span className="num">{typeof v === "number" ? fmtNum(v) : v}</span>;
}

function toDate(v: Date | string): Date {
  return v instanceof Date ? v : new Date(v);
}

export function fmtTime(v: Date | string, tz: string = browserTimeZone()): string {
  return new Intl.DateTimeFormat(LOC, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: tz,
  }).format(toDate(v));
}

export function fmtDate(v: Date | string, tz: string = browserTimeZone()): string {
  return new Intl.DateTimeFormat(LOC, {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: tz,
  }).format(toDate(v));
}

export function fmtDateShort(v: Date | string, tz: string = browserTimeZone()): string {
  return new Intl.DateTimeFormat(LOC, {
    day: "numeric",
    month: "long",
    timeZone: tz,
  }).format(toDate(v));
}

/**
 * نص مدى زمني: «6:00 م – 8:00 م».
 * ⚠️ بيرجّع نص بس — لازم يتعرض جوّه <TimeRange> أو <bdi> بلا dir.
 */
export function formatTimeRangeText(
  from: Date | string,
  to: Date | string,
  tz: string = browserTimeZone(),
): string {
  // الشرطة U+2013 محاطة بمسافات — مسافة عادية عشان ما تتكسرش على السطر جوّه bdi
  return `${fmtTime(from, tz)} – ${fmtTime(to, tz)}`;
}

/**
 * ✅ الطريقة الوحيدة المسموحة لعرض موعد أو مدى زمني.
 * <bdi> **بلا dir** — العزل لوحده بيصلّح الترتيب، وdir="ltr" بيكسره.
 */
export function formatTimeRange(
  from: Date | string,
  to: Date | string,
  tz?: string,
): ReactElement {
  return <bdi className="num">{formatTimeRangeText(from, to, tz)}</bdi>;
}

/** موعد مفرد — نفس القاعدة: <bdi> بلا dir لأن «م/ص» حرف عربي */
export function TimeAt({ at, tz }: { at: Date | string; tz?: string }): ReactElement {
  return <bdi className="num">{fmtTime(at, tz)}</bdi>;
}

/**
 * لاتيني صافي بس: تليفون · بريد · معرّف · «C1» · «4:12».
 * ⛔ ممنوع تمرير أي نص فيه حرف عربي لده.
 */
export function Ltr({ children }: { children: string }): ReactElement {
  // 🔴 **بيصلّح نفسه مش بيحذّر بس.**
  //    الإصدار القديم كان بيطبع console.error وبعدين **ينفّذ الغلط برضه** —
  //    يعني النص بيتقلب في الإنتاج وفي وش المستخدم، والتحذير في كونسول
  //    محدش فاتحه (وأصلًا `import.meta.env.DEV` بيطفيه في الإنتاج).
  //    الفخ ده ضرب **٥ مرات** في المشروع؛ آخرها اسم تبويبة عربي في ملف
  //    الطالب. الحماية الوحيدة اللي بتنفع هي إن المكوّن **ما ينفّذش الغلط**.
  //    `dir="ltr"` جوّاه عربي بيقلب ترتيب المقاطع: «6م – 8م» ⇒ «م – 8م6».
  const hasArabic = /[\u0600-\u06FF\u0750-\u077F\uFB50-\uFDFF\uFE70-\uFEFF]/.test(
    children,
  );
  if (hasArabic) {
    if (import.meta.env.DEV) {
      console.error(
        "[bidi] <Ltr> اتنده بنص فيه حرف عربي — اتحوّل لـ<bdi> تلقائيًا. صلّح المصدر:",
        children,
      );
    }
    // ⛔ **بلا `dir`** — العزل من غير فرض اتجاه: الترتيب بيفضل صح.
    return <bdi className="num">{children}</bdi>;
  }
  return (
    <bdi dir="ltr" className="num">
      {children}
    </bdi>
  );
}

/** «من 20 دقيقة» · «من 3 ساعات» — نص نسبي بأرقام لاتينية */
export function fmtRelative(v: Date | string, ref: Date = new Date()): string {
  const diffMs = ref.getTime() - toDate(v).getTime();
  const min = Math.round(diffMs / 60000);
  if (min < 1) return "دلوقتي";
  if (min < 60) return `من ${fmtNum(min)} دقيقة`;
  const hours = Math.round(min / 60);
  if (hours < 24) return hours === 1 ? "من ساعة" : `من ${fmtNum(hours)} ساعات`;
  const days = Math.round(hours / 24);
  return days === 1 ? "من يوم" : `من ${fmtNum(days)} أيام`;
}
